import { useMemo } from 'react'
import type { RaRecord } from 'react-admin'
import { useGetList } from 'react-admin'

import type { ActiveInspection } from '../models/activeInspection'
import { ACTIVE_INSPECTION_COLUMNS, activeInspectionRowSchema, convertToActiveInspection } from '../models/activeInspection'

/**
 * Upper bound on rows fetched in one go. The `inspections` table holds only open
 * inspections plus the current day's finished/cancelled ones — a nightly job
 * deletes FINALIZADO/ANULADO rows from previous days — so the whole table
 * comfortably fits in a single request and can be filtered and sorted locally.
 */
export const ACTIVE_INSPECTIONS_PAGE_SIZE = 1000

/** A row the API returned that could not be interpreted, and was dropped. */
export interface SkippedInspection {
  id: string
  /** Failing field paths and messages, so the drift is diagnosable from the UI. */
  reason: string
}

export interface UseActiveInspectionsResult {
  inspections: ActiveInspection[]
  skipped: SkippedInspection[]
  isPending: boolean
  error: unknown
  total: number
}

const describeParseFailure = (issues: { path: PropertyKey[]; message: string }[]): string =>
  issues.map(issue => `${issue.path.join('.') || '(raíz)'}: ${issue.message}`).join('; ')

export const useActiveInspections = (): UseActiveInspectionsResult => {
  const { data, total, isPending, error } = useGetList<RaRecord>('inspections', {
    pagination: { page: 1, perPage: ACTIVE_INSPECTIONS_PAGE_SIZE },
    sort: { field: 'day', order: 'DESC' },
    meta: { columns: [...ACTIVE_INSPECTION_COLUMNS] },
  })

  return useMemo(() => {
    const inspections: ActiveInspection[] = []
    const skipped: SkippedInspection[] = []

    for (const row of data ?? []) {
      const id = typeof row.id === 'string' ? row.id : String(row.id)
      const parsed = activeInspectionRowSchema.safeParse(row)

      if (!parsed.success) {
        skipped.push({ id, reason: describeParseFailure(parsed.error.issues) })
        continue
      }

      try {
        inspections.push(convertToActiveInspection(parsed.data))
      } catch (conversionError) {
        // A row whose shape is right but whose meaning is not — a status
        // carrying no lifecycle meaning for the type it is filed under. It joins
        // the shape failures in the same warning rather than taking the page
        // down with it, or wearing a state nothing assigned it.
        skipped.push({ id, reason: conversionError instanceof Error ? conversionError.message : String(conversionError) })
      }
    }

    return { inspections, skipped, isPending, error, total: total ?? inspections.length }
  }, [data, total, isPending, error])
}
